// Copyright 2017 Sorama B.V.

import {
    BindingEngine,
    Disposable,
    autoinject,
    bindable,
    computedFrom,
    observable,
    transient,
} from "aurelia-framework";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { AnalysisWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/AnalysisWorkflowModuleCatalog";
import { GridDataPlayer } from "controls/griddataplayer/GridDataPlayer";
import { Grid } from "controls/plot/grid-plot/GridData";
import { LegendEntry, LegendGroup } from "controls/plot/parts/LegendPart";
import { PointSelection } from "controls/plot/parts/PointSelectionPart";
import { As2DeviceType } from "datatypes/FlukeAs2Implementation";
import { MeasurementProcessorTypes } from "datatypes/MeasurementProcessorTypes";
import { Range as IRange, Resolution } from "datatypes/SoramaLocalApi";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { IPlayablePropertiesService } from "services/PlayableProperties/IPlayablePropertiesService";
import { IBeamformingProcessingService } from "services/SoundProcessing/IBeamformingProcessingService";
import { IMeasurementVideoFileService } from "services/Storage/IMeasurementVideoFileService";

import {
    BeamformingProjectionAnalysisProperty,
    BeamformingProjectionSet,
    BeamformingProjectionSetAnalysisProperties,
    CameraProperties,
    GenericMeasurement,
    MeasurementPart,
    MeasurementPartType,
    Point3D,
    VideoFrame,
    WebCamDeviceProperties,
} from "datatypes/SoramaWebApi";
import { FileNameConversion } from "extensions/FileNameConversion";
import { MeasurementExtensions } from "extensions/MeasurementExtensions";
import { StringExtensions } from "extensions/StringExtensions";
import { CSVDataExporter } from "libraries/Export/CSVDataExporter";
import { IExportable } from "libraries/Export/IExportable";
import { SVGNode } from "libraries/SVG/SVGNode";
import { VideoCalibration } from "libraries/VideoProperties/VideoCalibration";
import {
    IWorkflowModuleActivate,
    IWorkflowModuleExportData,
    IWorkflowModuleExportVisual,
    IWorkflowModuleInstance,
    IWorkflowModuleModalInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import {
    WorkflowPropertyBindingMode,
    WorkflowPropertyScope,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { userSetting } from "libraries/WorkflowFramework/UserSettingsAttribute";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import {
    BeamformProbeModalReturnState,
    BeamformProbeModalState,
} from "modules/modals/BeamformProbe/BeamformProbeModal";
import { ExportModalState, IExportModule } from "modules/modals/Export/ExportModal";
import { ImageExportState, LogoPositions } from "modules/modals/ImageExport/ImageExportModal";
import { SettingsModalState } from "modules/modals/SettingsModal";
import { MeasurementField, MeasurementRepository } from "repositories/MeasurementRepository";
import { MeasurementColorService } from "services/Color/MeasurementColorService";
import { IColorMap } from "services/ColorMap/IColorMap";
import { IColorMapService } from "services/ColorMap/IColorMapService";
import { JetColorMap } from "services/ColorMap/Predefined/JetColorMap";
import { FrequencySelectionService } from "services/FrequencySelection/FrequencySelectionService";
import { FrequencySuggestionService } from "services/FrequencySelection/FrequencySuggestionService";
import { NotificationUrgency } from "services/Notification/INotificationService";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";
import { Length } from "units/Length";
import { Pressure } from "units/Pressure";
import { Time } from "units/Time";
import { Unit, UnitValue } from "units/Unit";
import { FarfieldProjectionModel } from "./FarfieldProjectionModel";

interface GenericMeasurementWithPosition extends PointSelection, GenericMeasurement {}

export interface SoundStreamId {
    id: string;
    name: string;
}

@transient()
@autoinject
export class FarfieldModule
    implements IWorkflowModuleExportVisual, IWorkflowModuleExportData, IWorkflowModuleActivate, IExportModule
{
    //#region Constants

    private static dataColorMapSettingKey = "FarfieldAnalysisModuleDataColorMap";

    private static instrumentTypesWithoutVideo = [As2DeviceType.Ii900, As2DeviceType.Ii910];

    private static maximumResolutionDimension = 48;

    private readonly defaultVideoResolution: Resolution = { width: 1280, height: 720 };
    private videoResolution: Resolution = this.defaultVideoResolution;

    @computedFrom("videoResolution")
    public get aspectRatio(): number {
        return this.videoResolution ? this.videoResolution.width / this.videoResolution.height : 16 / 9;
    }

    @computedFrom("measurement")
    public get measurementDistance(): number {
        return this.measurement.distance !== undefined && this.measurement.distance !== null
            ? this.measurement.distance
            : 10.0;
    }

    //#endregion

    //#region  Module properties
    public soundStreamIds: SoundStreamId[] = [{ id: "", name: "" }];
    private moduleInstance: IWorkflowModuleInstance;
    private farfieldSettingsModal: IWorkflowModuleModalInstance<SettingsModalState<FarfieldModule>, void>;

    @computedFrom("busy", "currentProjection")
    public get disableExport(): boolean {
        return this.busy || !this.currentProjection;
    }

    @computedFrom("animator")
    public get canExportVideo(): boolean {
        return !!this.animator;
    }

    //#endregion

    //#region Synchronized time stuff
    public localPlayableId: string;
    public globalPlayableId: string = IPlayablePropertiesService.GlobalTimePlayablePropertyId;
    public playableIdFarfield = IPlayablePropertiesService.GlobalTimePlayablePropertyId;
    //#endregion

    //#region UI Properties

    /** The distance set across modules  */
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, scope: WorkflowPropertyScope.workflowModule })
    private distance: number;

    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        scope: WorkflowPropertyScope.workflowModule,
        defaultValue: false,
    })
    private averagedResponse: boolean;

    @observable @userSetting() public fps = 25;
    @userSetting() public opacity = 60;
    @observable @userSetting() public smoothingTime = 0;

    public smoothingOptions: string[] = ["None", "Impulse", "Fast", "Slow"];
    private static smoothingValues: Record<string, number> = {
        None: 0,
        Impulse: 0.035,
        Fast: 0.125,
        Slow: 1.0,
    };

    @observable
    public SmoothingOption: string = "None";

    public SmoothingOptionChanged(newValue: string): void {
        this.smoothingTime = FarfieldModule.smoothingValues[newValue] ?? 0;
    }

    @userSetting() public threshold = 30;
    @userSetting() public ticksFarfield = false;
    @userSetting() public probes = true;
    @userSetting() public showLegendFarfield = false;
    @userSetting() public legendPosition: number;

    public unitOptions = [Pressure.pascal, Pressure.dbSPL];

    @observable
    public unitToDisplay: Unit = this.workflowModuleService.selectedWorkflowMode.isIndustrial
        ? Pressure.dbSPL
        : Pressure.pascal;

    @bindable
    public dataDisplayUnit: Unit = Pressure.pascal;

    public timeDisplayUnit: Unit = Time.second;

    @observable @userSetting() public videoOffsetMs = 0;
    public legendArray: LegendGroup[] = [];

    @observable
    private dataColorMap: IColorMap = new JetColorMap();

    private decimals = "scientific";

    private frequenciesHaveChanged = false;
    private timeRangeHasChanged = false;
    private distanceHasChanged = false;
    private averageResponseHasChanged = false;
    private measurementHasChanged = false;

    public progress = 0;
    public busy = false;
    public hasFailed = false;
    public fpsHasChanged = false;

    public grid: Grid;
    public currentProjection?: FarfieldProjectionModel;

    @computedFrom(
        "frequenciesHaveChanged",
        "timeRangeHasChanged",
        "distanceHasChanged",
        "measurementHasChanged",
        "averageResponseHasChanged",
        "fpsHasChanged"
    )
    public get propertiesHaveChanged(): boolean {
        return (
            this.frequenciesHaveChanged ||
            this.timeRangeHasChanged ||
            this.distanceHasChanged ||
            this.measurementHasChanged ||
            this.averageResponseHasChanged ||
            this.fpsHasChanged
        );
    }

    public imageState: { state: ImageExportState; moduleInstance: IWorkflowModuleInstance } = {
        state: null,
        moduleInstance: null,
    };

    private ffExportModal: IWorkflowModuleModalInstance<ExportModalState<IExportModule>, void>;

    @computedFrom("currentProbeMeasurements", "temporaryProbeIndicator")
    public get probePositions(): PointSelection[] {
        if (!this.currentProbeMeasurements) {
            return [];
        }

        if (this.temporaryProbeIndicator) return [...this.currentProbeMeasurements, this.temporaryProbeIndicator];

        return this.currentProbeMeasurements;
    }

    @computedFrom("currentMeasurement")
    public get noVideoErrorMessage(): string[] {
        const message = ["The selected measurement does not contain a video."];

        const instrumentType = this.currentMeasurement?.specialMeasurementInfo?.metadata?.instrument?.type;
        if (!!instrumentType && FarfieldModule.instrumentTypesWithoutVideo.some((t) => t == instrumentType)) {
            message.push(
                `Note: Measurements from ${StringExtensions.enumerate(
                    FarfieldModule.instrumentTypesWithoutVideo
                )} devices do not contain video data.`
            );
        }

        return message;
    }

    //#endregion

    //#region Fields

    private measurementUpdateDisposables: Disposable[] = [];
    private probeMeasurementUpdateDisposables: Disposable[] = [];
    private observerSubscriptions: Disposable[] = [];

    private beamformingFrames: BeamformingProjectionSet;
    private videoFrames: VideoFrame[];

    private measurementId: string;

    private measurement: GenericMeasurement;
    private currentMeasurement: GenericMeasurement;

    @observable
    private currentProbeMeasurements: GenericMeasurementWithPosition[];
    private temporaryProbeIndicator: PointSelection;

    private videoParts: MeasurementPart[];
    private selectedVideoPart: MeasurementPart;

    private currentlyselectedActiveFrequencies: IRange[] = [];
    private useDefaultDistance = true;

    /** The distance shown in the legend of the previous computed farfield */
    private currentDistance: number;

    private currentVideoMeasurementId: string;

    private animator: GridDataPlayer;
    private dataExportable: IExportable;
    private colorbarExportable: IExportable;

    private gridplotElement: HTMLElement;

    @computedFrom("workflowModuleService.selectedWorkflowMode")
    private get autoPlay(): boolean {
        return this.workflowModuleService.selectedWorkflowMode.isIndustrial;
    }

    //#endregion

    //#region Workflow Properties

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.oneWay, objectType: "string" })
    public selectedMeasurementIds: string[];

    //#endregion

    //#region Constructor and Initialization
    constructor(
        private readonly processorResolver: IMeasurementProcessorResolver,
        private readonly measurementRepository: MeasurementRepository,
        private readonly measurementColorService: MeasurementColorService,
        private readonly colorMapService: IColorMapService,
        private readonly bindingEngine: BindingEngine,
        private readonly workflowModuleService: WorkflowModeService,
        private readonly frequencySelectionService: FrequencySelectionService,
        private readonly frequencySuggestionService: FrequencySuggestionService
    ) {}

    public bind(): void {
        // listen to changes in frequency ranges
        this.observerSubscriptions.push(
            this.bindingEngine
                .collectionObserver(this.frequencySelectionService.ranges)
                .subscribe(() => this.selectedActiveFrequenciesChanged())
        );
        this.observerSubscriptions.push(
            this.bindingEngine
                .propertyObserver(this.frequencySuggestionService, "areSuggestionsComputed")
                .subscribe(() => this.frequencySuggestionsComputed())
        );
    }

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        const promises = [
            this.colorMapService.getDefaultColorMap(FarfieldModule.dataColorMapSettingKey).then((colorMap) => {
                this.dataColorMap = colorMap;
            }),
        ];

        Promise.all(promises)
            .then(() => {
                // Set flag to use the default distance for a measurement
                this.useDefaultDistance = true;
            })
            .then(() => {
                this.selectedActiveFrequenciesChanged();
                this.selectedMeasurementIdsChanged();
            });
    }

    public unitToDisplayChanged(newValue: Unit, oldValue: Unit) {
        if (newValue != Pressure.pascal) {
            this.decimals = "1";
        } else this.decimals = "scientific";
    }

    protected onAttached(): void {
        while (this.measurementUpdateDisposables.length > 0) {
            this.measurementUpdateDisposables.pop().dispose();
        }

        while (this.probeMeasurementUpdateDisposables.length > 0) {
            this.probeMeasurementUpdateDisposables.pop().dispose();
        }

        while (this.observerSubscriptions.length > 0) {
            this.observerSubscriptions.pop().dispose();
        }
    }
    //#endregion

    //#region Update functions

    private async updateData(dontRetry?: boolean): Promise<void> {
        if (!this.measurementId) return Promise.resolve();

        this.progress = 0;
        this.busy = true;
        const measurementChanged = this.currentMeasurement !== this.measurement;

        try {
            // Start by making sure we have the proper video data, since its field of view is leading for the computation
            await this.updateVideo();

            const promises = [this.updateFarfield(), this.updateProbes(measurementChanged)];

            await Promise.all(promises);

            if (!this.grid || !this.beamformingFrames) {
                this.currentProjection = undefined;
            } else {
                this.currentProjection = new FarfieldProjectionModel(
                    this.grid,
                    this.currentDistance,
                    this.beamformingFrames,
                    this.videoFrames,
                    new UnitValue(this.videoOffsetMs, Time.milliSecond)
                );
                this.currentProjection.smoother.timeConstant = this.smoothingTime;
            }

            this.currentMeasurement = this.measurement;
            this.measurementHasChanged = false;
            this.timeRangeHasChanged = false;

            this.moduleInstance.createTimelineEvent();

            if (this.autoPlay && this.animator) {
                this.animator.resetPlayback();
                this.animator.togglePlaying();
            }
        } catch (e) {
            if ((e.statusCode === 0 || e.statusCode === 404) && !dontRetry) {
                // Requeue the analysis
                this.updateData(true);
                return;
            } else {
                // check for cosmosdb timeout error, we don't want to manually retry immediately
                if (e.message?.includes("TooManyRequests")) {
                    e.message = "The server is currently too busy to process your request. Please try again later.";
                }
                this.moduleInstance
                    .createNotification()
                    .withUrgency(NotificationUrgency.Error)
                    .withMessage(`Unable to compute far field projection. ${e.message ?? ""}`)
                    .show();
            }
        } finally {
            this.busy = false;
        }
    }

    private updateFarfield(): Promise<void> {
        if (
            !this.frequencySelectionService.selectedRanges ||
            this.frequencySelectionService.selectedRanges.length == 0
        ) {
            return Promise.resolve();
        }

        const distance = this.distance;

        const calibration = VideoCalibration.create(
            FarfieldModule.getCameraPosition(this.measurement, this.selectedVideoPart),
            FarfieldModule.getBeamformingResolution(this.videoResolution),
            distance,
            <WebCamDeviceProperties | CameraProperties>(
                (this.selectedVideoPart ? this.selectedVideoPart.device.properties : undefined)
            )
        );

        // Generate new targetpoints for the current settings
        const properties = <BeamformingProjectionSetAnalysisProperties>{
            targetPoints: calibration.targetPoints,
            frameProperties: <BeamformingProjectionAnalysisProperty[]>[],
        };

        // Make a copy of the currently selected frequencies
        const frequencies = this.frequencySelectionService.selectedRanges
            .map((x) => {
                return {
                    from: x.from,
                    to: x.to,
                };
            })
            .sort((a, b) => a.from - b.from);

        // Generate frame times
        const duration = this.measurement.duration;
        const step = this.averagedResponse ? duration : 1.0 / this.fps;
        const count = this.averagedResponse ? 1 : Math.floor(duration * this.fps);
        for (let i = 0; i < count; i++) {
            properties.frameProperties.push(<BeamformingProjectionAnalysisProperty>{
                frequencies: frequencies,
                timeInterval: <IRange>{
                    from: i * step,
                    to: (i + 1) * step,
                },
            });
        }

        this.progress = 0;

        // Get the actual data
        return this.processorResolver
            .resolveProcessor<IBeamformingProcessingService>(IBeamformingProcessingService, this.measurement)
            .then((service) => {
                // Compute beamforming projections on processor
                return service
                    .getBeamformingProjections(this.measurement, properties, (progress: number) => {
                        // Update progress
                        this.progress = progress * 100;
                    })
                    .then((set) => {
                        // Update all settings used for the computation
                        this.updateGrid(calibration.grid);
                        this.currentDistance = distance;
                        this.currentlyselectedActiveFrequencies = frequencies;

                        this.legendArray = [
                            {
                                title: "Distance",
                                entries: [{ name: this.currentDistance.toString() + " m" }],
                            },
                            {
                                title: "Frequencies",
                                entries: this.currentlyselectedActiveFrequencies.map((x): LegendEntry => {
                                    return { name: x.from.toFixed(0) + " - " + x.to.toFixed(0) + " Hz" };
                                }),
                            },
                        ];

                        // Unset changed flags
                        this.frequenciesHaveChanged = false;
                        this.distanceHasChanged = false;
                        this.averageResponseHasChanged = false;
                        this.fpsHasChanged = false;

                        this.beamformingFrames = set;
                    });
            });
    }

    private updateVideo(): Promise<void> {
        if (this.currentVideoMeasurementId === this.measurement.id) {
            return Promise.resolve(); // We already have this video
        }

        if (!this.selectedVideoPart) {
            this.videoResolution = this.defaultVideoResolution;
            return Promise.resolve();
        }

        const videoPartIndex = this.measurement.parts.findIndex((x) => x === this.selectedVideoPart);

        if (videoPartIndex === -1) return Promise.reject("Measurement does not contain video data");

        // Get the correct processor for this measurement
        return this.processorResolver
            .resolveProcessor<IMeasurementVideoFileService>(IMeasurementVideoFileService, this.measurement)
            .then((service) => {
                // Get the frames
                return service
                    .getVideoFrames(this.measurement, videoPartIndex, undefined, 0, this.measurement.duration)
                    .then((allFrames) => {
                        this.videoFrames = <VideoFrame[]>(<unknown>allFrames[0]);

                        return new Promise<void>((resolve, reject) => {
                            // Load a singe frame and get the resolution, since it is not a property in the metadata.
                            const frame = this.videoFrames[0];
                            const img = new Image();

                            img.onerror = reject;
                            img.onload = () => {
                                this.videoResolution = {
                                    width: img.width,
                                    height: img.height,
                                };

                                resolve();
                            };

                            img.src = "data:image/jpg;base64," + frame.data;
                            this.currentVideoMeasurementId = this.measurement.id;
                        });
                    });
            });
    }

    private async updateProbes(measurementChanged: boolean): Promise<void> {
        let probeMeasurements: GenericMeasurementWithPosition[];
        if (!measurementChanged && this.currentProbeMeasurements) {
            probeMeasurements = this.currentProbeMeasurements;
        } else {
            probeMeasurements = await this.measurementRepository.getComputedMeasurements(
                this.measurement.id,
                MeasurementProcessorTypes.beamformProbe
            );
        }

        if (!probeMeasurements || probeMeasurements.length == 0) return;

        for (const measurement of probeMeasurements) {
            // Assign a color to the current measurement
            this.measurementColorService.ensureAssignedColor({ measurementId: measurement.id });

            const isSoundStream =
                this.soundStreamIds != undefined &&
                this.soundStreamIds.findIndex((x) => x.id == measurement.id) == -1 &&
                measurement.computedMeasurementSourceId == this.soundStreamIds[1].id;

            if (isSoundStream) {
                //Add all probe measurements to audioStreams selection menu
                this.soundStreamIds.push({ id: measurement.id, name: measurement.name });
            }
        }

        this.refreshProbeMeasurementUpdateObservers(probeMeasurements);

        this.currentProbeMeasurements = probeMeasurements.map(this.toGenericMeasurementWithPosition);
        this.temporaryProbeIndicator = undefined;
    }

    private refreshProbeMeasurementUpdateObservers(newProbes: GenericMeasurementWithPosition[]): void {
        // Register measurement update observers for each of the probes
        while (this.probeMeasurementUpdateDisposables.length > 0) {
            this.probeMeasurementUpdateDisposables.pop().dispose();
        }

        for (const probe of newProbes) {
            this.probeMeasurementUpdateDisposables.push(
                this.measurementRepository.measurementUpdateObserver(this.measurementUpdated, probe.id)
            );
        }
    }

    private updateGrid(grid: Grid): Promise<void> {
        this.grid = grid;

        if (this.moduleInstance != undefined) {
            return this.moduleInstance.dispatch(() => this.updateProbes(false)).catch(() => {});
        }

        return Promise.resolve();
    }
    //#endregion

    //#region Changed callbacks

    public distanceChanged(): void {
        const measurementDistanceSame = Math.abs(this.distance - this.currentDistance) <= Number.EPSILON * 2;
        if (this.useDefaultDistance && this.measurement && !measurementDistanceSame) {
            this.useDefaultDistance = false;
        }

        this.distanceHasChanged = this.hasDistanceChanged();
    }

    private hasDistanceChanged(): boolean {
        const currentDistanceSame = Math.abs(this.distance - this.currentDistance) <= Number.EPSILON * 2;
        if (this.currentDistance && currentDistanceSame) {
            return false;
        }

        // If there is no currently computed measurement, update the grid directly
        if (!this.beamformingFrames) {
            const calibration = VideoCalibration.create(
                FarfieldModule.getCameraPosition(this.measurement, this.selectedVideoPart),
                FarfieldModule.getBeamformingResolution(this.videoResolution),
                this.distance,
                <WebCamDeviceProperties | CameraProperties>(
                    (this.selectedVideoPart ? this.selectedVideoPart.device.properties : undefined)
                )
            );

            this.updateGrid(calibration.grid);
        }

        return true;
    }

    private frequencySuggestionsComputed(): void {
        if (this.frequencySuggestionService.areSuggestionsComputed) {
            this.dispatchUpdateData();
        }
    }

    public selectedActiveFrequenciesChanged(): void {
        if (this.frequencySelectionService.selectedRanges === this.currentlyselectedActiveFrequencies) {
            this.frequenciesHaveChanged = false;
            return;
        }

        // Check if the frequencies have changed
        if (!this.frequencySelectionService.selectedRanges || !this.currentlyselectedActiveFrequencies) {
            this.frequenciesHaveChanged = true;
            return;
        }

        this.frequenciesHaveChanged = !this.rangesAreSameWithErrorMargin(
            this.frequencySelectionService.selectedRanges,
            this.currentlyselectedActiveFrequencies,
            Number.EPSILON * 2
        );
    }

    private rangesAreSameWithErrorMargin(aRange: IRange[], bRange: IRange[], errorMargin: number): boolean {
        if (aRange.length !== bRange.length) return false;

        const aRangeCopy = [...aRange];

        for (const b of bRange) {
            const matchingIndex = aRangeCopy.findIndex(
                (a) => Math.abs(b.from - a.from) <= errorMargin && Math.abs(b.to - a.to) <= errorMargin
            );

            if (matchingIndex === -1) {
                return false;
            }

            aRangeCopy.splice(matchingIndex, 1);
        }

        return aRangeCopy.length === 0;
    }

    public selectedMeasurementIdsChanged(): void {
        if (!this.selectedMeasurementIds || this.selectedMeasurementIds.length == 0) {
            this.clearState();
            return;
        }

        this.measurementHasChanged = this.selectedMeasurementIds[0] !== this.measurementId;
        if (this.measurementHasChanged && this.moduleInstance) {
            this.measurementId = this.selectedMeasurementIds[0];
            this.moduleInstance.dispatch(() => {
                this.busy = true;

                return this.measurementRepository.getMeasurement(this.measurementId).then((measurement) => {
                    this.measurement = measurement;

                    // Register listener for changes on the measurement
                    while (this.measurementUpdateDisposables.length > 0) {
                        this.measurementUpdateDisposables.pop().dispose();
                    }

                    this.measurementUpdateDisposables.push(
                        this.measurementRepository.measurementUpdateObserver(this.measurementUpdated, measurement.id)
                    );

                    // Assign a color to the current measurement
                    this.measurementColorService.ensureAssignedColor({ measurementId: measurement.id });

                    //First value in array is an empty object which represents "no audiostream selected"
                    this.soundStreamIds = [{ id: "", name: "" }];

                    //Add global measurement name/id as second object in soundStreams array
                    this.soundStreamIds.push({ id: this.measurement.id, name: this.measurement.name });

                    if (this.useDefaultDistance) {
                        this.distance = this.measurementDistance;
                    }

                    // Check if there is video
                    if (this.measurement.parts.some((x) => x.measurementType === MeasurementPartType.Video)) {
                        // Set default video part
                        this.videoParts = this.measurement.parts.filter(
                            (x) => x.measurementType === MeasurementPartType.Video
                        );
                        const videoPartIndex = this.measurement.parts.findIndex(
                            (x) => x.measurementType === MeasurementPartType.Video
                        );
                        this.selectedVideoPart = this.measurement.parts[videoPartIndex];
                    } else {
                        this.videoParts = undefined;
                        this.selectedVideoPart = undefined;
                        this.currentVideoMeasurementId = undefined;
                        this.videoFrames = [];
                    }

                    this.busy = false;

                    return this.updateData();
                });
            });
        }
    }

    private clearState(): void {
        this.measurement = undefined;
        this.measurementId = undefined;
        this.currentMeasurement = undefined;
        this.currentProjection = undefined;
        this.selectedVideoPart = undefined;
        this.currentVideoMeasurementId = undefined;
        this.currentProbeMeasurements = [];
        this.beamformingFrames = undefined;
        this.grid = undefined;
        this.videoFrames = undefined;
        this.soundStreamIds = undefined;
    }

    public measurementUpdated = (property: MeasurementField, measurement: GenericMeasurement): void => {
        // Update measurement fields
        if (this.measurement) {
            if (
                !MeasurementExtensions.updateMeasurementField(
                    this.measurement,
                    measurement.id,
                    property,
                    measurement[property]
                )
            ) {
                // Check probe measurements
                for (const probe of this.currentProbeMeasurements) {
                    if (
                        MeasurementExtensions.updateMeasurementField(
                            probe,
                            measurement.id,
                            property,
                            measurement[property]
                        )
                    ) {
                        break;
                    }
                }
            }
        }

        // Check if we need to invalidate some data
        if (
            property === MeasurementField.mutedSensors &&
            MeasurementExtensions.isRelatedTo(this.currentMeasurement, measurement.id)
        ) {
            this.measurementHasChanged = true;
        }

        if (
            property == MeasurementField.distance &&
            MeasurementExtensions.isRelatedTo(this.currentMeasurement, measurement.id)
        ) {
            this.measurementHasChanged = true;
        }
    };

    public dataColorMapChanged(): void {
        if (this.colorMapService) {
            this.colorMapService.setDefaultColorMap(FarfieldModule.dataColorMapSettingKey, this.dataColorMap);
        }
    }

    public averagedResponseChanged(): void {
        this.averageResponseHasChanged = !this.averageResponseHasChanged;
    }

    public fpsChanged(): void {
        this.fpsHasChanged = true;
    }

    public videoOffsetMsChanged(): void {
        if (this.currentProjection) {
            this.currentProjection.videoOffset = new UnitValue(this.videoOffsetMs, Time.milliSecond);
        }
    }

    public smoothingTimeChanged(newValue: number): void {
        if (this.currentProjection) {
            this.currentProjection.smoother.timeConstant = newValue;
        }
    }

    //#endregion

    //#region UI Related

    public dispatchUpdateData(): void {
        this.updateData();
    }

    public positionToTooltipClass(position: { xCoordinate: number; yCoordinate: number }): string {
        const sizedElement = this.gridplotElement.children[0].children[0].children[1];

        if (position.xCoordinate < 100) {
            return "tooltip-right";
        } else if (position.xCoordinate > sizedElement.clientWidth - 100) {
            return "tooltip-left";
        } else if (position.yCoordinate > sizedElement.clientHeight - 100) {
            return "tooltip-top";
        } else {
            return "tooltip-bottom";
        }
    }

    public setDefaultDistance(): void {
        this.useDefaultDistance = true;
        if (this.measurement) this.distance = this.measurementDistance;
    }

    public addProbe = (source: unknown, point: { x?: UnitValue; y?: UnitValue }) => {
        if (!this.currentMeasurement) return;

        // Round target on 3 decimals
        const x = Math.round(point.x.value * 10000) / 10000;
        const y = Math.round(point.y.value * 10000) / 10000;
        const z =
            Math.round((this.currentDistance !== undefined ? this.currentDistance : this.measurementDistance) * 10000) /
            10000;

        // Add a temporary indicator to show the user where he/she clicked
        const tempProbe = this.computeRelativePosition({
            X: x,
            Y: y,
            Z: z,
        });
        this.temporaryProbeIndicator = tempProbe;

        this.moduleInstance
            .createModal<BeamformProbeModalState, BeamformProbeModalReturnState>(
                WorkflowModalCatalog.BeamformProbeModal
            )
            .withState({
                measurement: this.currentMeasurement,
                targetPoint: <Point3D>{
                    x: x,
                    y: y,
                    z: z,
                },
            })
            .whenClosed((returnState) => {
                return this.measurementRepository
                    .createBeamformProbeGenericMeasurement(returnState.name, this.currentMeasurement.id, {
                        targetPoint: returnState.targetPoint,
                    })
                    .then((x: GenericMeasurementWithPosition) => {
                        this.currentProbeMeasurements.push(this.toGenericMeasurementWithPosition(x));

                        // Navigate to the Analysis Overview
                        if (returnState.navigateToOverview) {
                            const modules = this.moduleInstance
                                .getWorkflow()
                                .getModules(AnalysisWorkflowModuleCatalog.AnalysisOverviewModule);
                            if (modules.length > 0) modules[0].makeActive();
                        }

                        this.updateProbes(false);
                    })
                    .catch((e: Error) => {
                        this.moduleInstance
                            .createNotification()
                            .withUrgency(NotificationUrgency.Error)
                            .withMessage(
                                "Unable to create probe analysis." +
                                    (e.message ? "The following message was provided: '" + e.message + "'." : "")
                            )
                            .show();
                    });
            })
            .whenCanceled(() => {
                this.temporaryProbeIndicator = undefined;
            })
            .show();
    };

    public selectProbe = (measurement: GenericMeasurementWithPosition): void => {
        if (this.temporaryProbeIndicator === <PointSelection>measurement) {
            return;
        }

        if (!this.currentMeasurement) {
            return;
        }

        this.moduleInstance
            .createModal<BeamformProbeModalState, BeamformProbeModalReturnState>(
                WorkflowModalCatalog.BeamformProbeModal
            )
            .withState({
                measurement: this.currentMeasurement,
                probeMeasurement: measurement,
            })
            .whenClosed((newState) => {
                if (newState.delete) {
                    // Delete the probe measurement
                    return this.measurementRepository
                        .deleteComputedMeasurement(newState.probeMeasurement.id)
                        .then(() => {
                            const index = this.currentProbeMeasurements.findIndex(
                                (x) => x.id === newState.probeMeasurement.id
                            );
                            const indexSoundStreams = this.soundStreamIds.findIndex(
                                (x) => x.id === newState.probeMeasurement.id
                            );

                            if (index === -1) return;

                            this.currentProbeMeasurements.splice(index, 1);
                            this.soundStreamIds.splice(indexSoundStreams, 1);
                        })
                        .catch((e) => {
                            this.moduleInstance
                                .createNotification()
                                .withUrgency(NotificationUrgency.Error)
                                .withMessage(
                                    "Unable to delete probe analysis. The following message was provided: '" +
                                        e.message +
                                        "'."
                                )
                                .show();
                        });
                } else if (newState.navigateToOverview) {
                    const modules = this.moduleInstance
                        .getWorkflow()
                        .getModules(AnalysisWorkflowModuleCatalog.AnalysisOverviewModule);
                    if (modules.length > 0) modules[0].makeActive();
                }
            })
            .show();
    };

    public exportToSvg = (): Promise<SVGNode> => {
        return Promise.all([this.dataExportable.exportToSvg(), this.colorbarExportable.exportToSvg()]).then((svgs) => {
            // Get svg from gridplot
            const farFieldSvg = svgs[0];

            // Get svg from colorbar
            const colorbarSvg = svgs[1];

            // Get size for both svgs
            const farFieldSize = farFieldSvg.bounds;
            const colorBarSize = colorbarSvg.bounds;

            // Now we manually need to merge the two svgs
            const newX = 0;
            const newY = 0;

            // We will scale the colorbar to fit the farField
            const colorBarScaling = farFieldSize.width / colorBarSize.width;
            const newWidth = farFieldSize.width;
            const newHeight = farFieldSize.height + colorBarSize.height * colorBarScaling;

            // Resize the farFieldsvg to be large enough for both
            farFieldSvg.setAttribute(
                "viewBox",
                newX.toFixed(2) + "," + newY.toFixed(2) + "," + newWidth.toFixed(2) + "," + newHeight.toFixed(2)
            );

            const translateX = ((newWidth - farFieldSize.width) / 2 - farFieldSize.x).toFixed(2);

            // Add two g elements to hold both sub-svgs
            let gNode = new SVGNode("g");
            gNode.setAttribute("transform", `translate(${translateX},${(-farFieldSize.y).toFixed(2)})`);
            for (const child of farFieldSvg.children) {
                gNode.addChild(child);
            }
            farFieldSvg.clearChildren();
            farFieldSvg.addChild(gNode);

            // Add a g element to hold the colorbar
            gNode = new SVGNode("g");
            gNode.setAttribute(
                "transform",
                `translate(${translateX},${farFieldSize.height.toFixed(2)}) scale(${colorBarScaling})`
            );
            for (const child of colorbarSvg.children) {
                gNode.addChild(child);
            }

            farFieldSvg.addChild(gNode);

            return farFieldSvg;
        });
    };

    public exportVisual(): ImageExportState {
        const exportModalState: ImageExportState = {
            measurementIds: [this.measurementId],
            initialFileName: this.measurement ? this.measurement.name + "_FarField" : undefined,
            initialLogoPosition: LogoPositions.topRight,
            isLegendDisplayed: false,
            animator: this.animator,
            exportable: this,
            namedProperties: [
                { name: "[Distance]", value: this.distance + Length.meter.unit },
                {
                    name: "[Frequencies]",
                    value: FileNameConversion.convertSelectedFrequencies(this.currentlyselectedActiveFrequencies),
                },
            ],
        };

        return exportModalState;
    }

    // Required by the export modal
    public csvExportCall = this.exportData;

    public exportData(): void {
        if (!this.currentProjection) return;

        this.currentProjection.exportData(
            new CSVDataExporter(),
            "farfield_" + MeasurementExtensions.toFileName(this.currentMeasurement)
        );
    }
    //#endregion

    //#region Helper Methods

    private static getCameraPosition(measurement?: GenericMeasurement, videoPart?: MeasurementPart): Point3D {
        if (!videoPart) {
            const arrayCenter = { x: 0, y: 0, z: 0 };

            if (measurement) {
                let totalPoints = 0;
                for (let i = 0; i < measurement.parts.length; i++) {
                    const positions = measurement.parts[i].device.sensorPositions;
                    for (let j = 0; j < positions.length; j++) {
                        const p = positions[j];
                        arrayCenter.x += p.x;
                        arrayCenter.y += p.y;
                        arrayCenter.z += p.z;
                        totalPoints++;
                    }
                }
                arrayCenter.x /= totalPoints;
                arrayCenter.y /= totalPoints;
                arrayCenter.z /= totalPoints;
            }

            return arrayCenter;
        }

        const sensorPosition = videoPart.device.sensorPositions[0];
        if (videoPart.position) {
            sensorPosition.x = sensorPosition.x + videoPart.position.x;
            sensorPosition.y = sensorPosition.y + videoPart.position.y;
            sensorPosition.z = sensorPosition.z + videoPart.position.z;
        }

        return sensorPosition;
    }

    private static getBeamformingResolution(videoResolution: Resolution): Resolution {
        const stepSize =
            Math.max(videoResolution.height, videoResolution.width) / FarfieldModule.maximumResolutionDimension;
        return {
            width: Math.round(videoResolution.width / stepSize),
            height: Math.round(videoResolution.height / stepSize),
        };
    }

    private toGenericMeasurementWithPosition = (measurement: GenericMeasurement): GenericMeasurementWithPosition => {
        const m = <GenericMeasurementWithPosition>measurement;

        const p = this.computeRelativePosition(m.computedMeasurementProperties.Properties.TargetPoint);
        m.x = p.x;
        m.y = p.y;

        return m;
    };

    private computeRelativePosition(point: { X: number; Y: number; Z: number }): { x: UnitValue; y: UnitValue } {
        const gridCenter = this.grid ? this.grid.center : { x: 0, y: 0 };

        return {
            x: new UnitValue(((point.X - gridCenter.x) / point.Z) * this.distance + gridCenter.x, Length.meter),
            y: new UnitValue(((point.Y - gridCenter.y) / point.Z) * this.distance + gridCenter.y, Length.meter),
        };
    }

    public openFarfieldSettingsModal(): void {
        if (this.farfieldSettingsModal && this.farfieldSettingsModal.isOpen) {
            this.farfieldSettingsModal.cancel();
            this.farfieldSettingsModal = undefined;
            return;
        }
        this.moduleInstance
            .createModal<SettingsModalState<FarfieldModule>, void>(WorkflowModalCatalog.FarfieldSettingsModal)
            .withState({ belongsTo: this })
            .whenClosed(() => {
                this.farfieldSettingsModal = undefined;
            })
            .show()
            .then((x) => {
                this.farfieldSettingsModal = x;
            });
    }

    public openExportModal(): void {
        if (this.ffExportModal && this.ffExportModal.isOpen) {
            this.ffExportModal.cancel();
            this.ffExportModal = undefined;
            return;
        }

        this.imageState.state = this.moduleInstance.getExportImageState();
        this.imageState.moduleInstance = this.moduleInstance;

        void this.moduleInstance
            .createModal<ExportModalState<IExportModule>, void>(WorkflowModalCatalog.ExportModal)
            .withState({ belongsTo: this })
            .whenClosed(() => {
                this.ffExportModal = undefined;
            })
            .show()
            .then((x) => {
                this.ffExportModal = x;
            });
    }
    //#endregion
}
